/**
 * Helpers for finding and removing references to a deleted Storage asset inside a sketch's file tree.
 *
 * A media file in a sketch stores the asset's Firebase download URL in `file.url`. That URL embeds the
 * Storage object path (`assets/{uid}/{id}.ext`) URL-encoded as `assets%2F{uid}%2F{id}.ext`. When the
 * underlying object is deleted, those file entries become dangling and must be pulled out of the tree.
 */

/**
 * Extract the Storage object path (`assets/{uid}/{id}.ext`) from a Firebase download URL, or null if
 * the url isn't a Firebase Storage download URL. This is the asset's `key` (matches AssetItem.key).
 */
export function storagePathFromUrl(url) {
  if (typeof url !== 'string') return null;
  const match = url.match(/\/o\/([^?]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch (e) {
    return null;
  }
}

/** Ids of the files in `files` whose `url` points at the Storage object identified by `assetKey`. */
export function findAssetFileIds(files, assetKey) {
  if (!assetKey) return [];
  const encoded = encodeURIComponent(assetKey);
  return (files || [])
    .filter(
      (file) =>
        file.fileType === 'file' &&
        typeof file.url === 'string' &&
        (file.url.includes(encoded) || file.url.includes(assetKey))
    )
    .map((file) => file.id);
}

/**
 * Upstream asset-listing logic: an asset's display name and owning sketch are derived from the project
 * file that references it (the sketch file is the source of truth for the name). Given raw Storage
 * objects (`{ key, size, url }`) and the user's projects (`{ id, name, files }`), return one entry per
 * REFERENCED object — objects that no sketch references are dropped (hidden), exactly like upstream.
 */
export function matchAssetsToProjects(objects, projects) {
  const matched = [];
  (objects || []).forEach((obj) => {
    let entry = null;
    (projects || []).some((project) => {
      const file = (project.files || []).find(
        (f) => storagePathFromUrl(f.url) === obj.key
      );
      if (file) {
        entry = {
          key: obj.key,
          name: file.name,
          size: obj.size,
          url: file.url,
          sketchName: project.name,
          sketchId: project.id
        };
        return true;
      }
      return false;
    });
    if (entry) matched.push(entry);
  });
  return matched;
}

/** Id of the file that lists `childId` in its `children`, or undefined if none does. */
export function findParentId(files, childId) {
  const parent = (files || []).find((file) =>
    (file.children || []).includes(childId)
  );
  return parent ? parent.id : undefined;
}

/**
 * Return a new files array with every entry in `ids` removed and its id pulled from any parent's
 * `children`. Asset files are leaves, so no descendant handling is needed.
 */
export function removeFilesFromTree(files, ids) {
  const removeSet = new Set(ids);
  return (files || [])
    .filter((file) => !removeSet.has(file.id))
    .map((file) => ({
      ...file,
      children: (file.children || []).filter((child) => !removeSet.has(child))
    }));
}
